import { useState } from 'react';
import { Folder, Users, Database } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { useWorkspaceSettings } from '../../stores/useSettingsStore.js';

const WorkspaceTab = () => {
  const { workspacePrefs, setWorkspacePrefs } = useWorkspaceSettings();
  const [workspace, setWorkspace] = useState({
    name: 'My HiveCore Workspace',
    description: 'Primary development workspace for AI automation projects',
    visibility: 'private',
    defaultTimeout: 30,
    maxAgents: 10,
    autoSave: workspacePrefs.autoSave,
    collaborativeMode: false
  });

  const [members] = useState([
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Owner', avatar: null },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Admin', avatar: null },
    { id: 3, name: 'Bob Wilson', email: 'bob@example.com', role: 'Member', avatar: null }
  ]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setWorkspace(prev => ({ ...prev, [field]: value }));
    
    if (field === 'autoSave') {
      setWorkspacePrefs({ autoSave: value as boolean });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Workspace</h2>
        <p className="text-slate-400 mt-1">Configure your workspace settings and team access</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Folder size={20} className="text-brand-accent" />
            <span>Workspace Settings</span>
          </CardTitle>
          <CardDescription>Basic workspace configuration and preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Workspace Name</label>
            <Input
              value={workspace.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="violett-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              value={workspace.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="violett-input min-h-[80px] resize-none"
              placeholder="Describe your workspace..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Visibility</label>
              <select
                value={workspace.visibility}
                onChange={(e) => handleInputChange('visibility', e.target.value)}
                className="violett-input"
              >
                <option value="private">Private</option>
                <option value="team">Team</option>
                <option value="public">Public</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Default Timeout (minutes)</label>
              <Input
                type="number"
                value={workspace.defaultTimeout}
                onChange={(e) => handleInputChange('defaultTimeout', parseInt(e.target.value))}
                className="violett-input"
                min="1"
                max="120"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Database size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Auto-save</div>
                <div className="text-xs text-slate-400">Automatically save workspace changes</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={workspace.autoSave}
                onChange={(e) => handleInputChange('autoSave', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Collaborative Mode</div>
                <div className="text-xs text-slate-400">Allow real-time collaboration</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={workspace.collaborativeMode}
                onChange={(e) => handleInputChange('collaborativeMode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="ghost">Reset</Button>
            <Button>Save Changes</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users size={20} className="text-brand-accent" />
            <span>Team Members</span>
          </CardTitle>
          <CardDescription>Manage workspace access and permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 mb-6">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-brand-text">{member.name}</div>
                    <div className="text-xs text-slate-400">{member.email}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    member.role === 'Owner' ? 'bg-purple-400/20 text-purple-400' :
                    member.role === 'Admin' ? 'bg-blue-400/20 text-blue-400' :
                    'bg-green-400/20 text-green-400'
                  }`}>
                    {member.role}
                  </span>
                  {member.role !== 'Owner' && (
                    <Button variant="ghost" size="sm">
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button className="violett-button text-drop-shadow w-full">
            <Users size={16} className="mr-2" />
            Invite Team Member
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkspaceTab;
